"use strict";
/**
 * Azure Voice configuration for voice chat functionality
 * Provides utilities for working with Azure Text-to-Speech voices
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.agentIds = exports.liveChats = exports.AzureVoicesHelper = exports.AVAILABLE_VOICES = void 0;
exports.findVoiceById = findVoiceById;
exports.getVoiceCategory = getVoiceCategory;
exports.getVoicesByCategory = getVoicesByCategory;
exports.getAllVoices = getAllVoices;
exports.getVoiceById = getVoiceById;
exports.getVoicesGroupedByGender = getVoicesGroupedByGender;
exports.getVoicesForGender = getVoicesForGender;
exports.isModelInElevenLabs = isModelInElevenLabs;
exports.isAvatarTalking = isAvatarTalking;
// Hardcoded Azure voices list
exports.AVAILABLE_VOICES = {
    male: [
        {
            name: 'en-GB-ElliotNeural',
            displayName: 'Elliot',
            locale: 'en-GB',
            language: 'English (United Kingdom)',
            gender: 'male',
            description: 'A middle-aged British male voice with a polished, professional tone, well-suited for narrating a documentary or an educational podcast.',
            style: 'Professional'
        },
        {
            name: 'de-DE-ConradNeural',
            displayName: 'Conrad',
            locale: 'de-DE',
            language: 'German (Germany)',
            gender: 'male',
            description: 'An engaging and friendly voice, perfect for maintaining a warm and inviting conversation.',
            style: 'Engaging',
            popular: true
        },
        {
            name: 'pl-PL-MarekNeural',
            displayName: 'Marek',
            locale: 'pl-PL',
            language: 'Polish (Poland)',
            gender: 'male',
            description: 'A middle-aged Polish male voice with a deep, authoritative tone, well-suited for news reporting or documentary narration.',
            style: 'Authoritative'
        },
        {
            name: 'zh-HK-YunSongNeural',
            displayName: 'YunSong',
            locale: 'zh-HK',
            language: 'Chinese (Cantonese, Simplified)',
            gender: 'male',
            description: 'A deep, calm, and formal voice, exuding authority and professionalism in a composed manner.',
            style: 'Deep'
        },
        {
            name: 'it-IT-MarcelloMultilingualNeural',
            displayName: 'Marcello Multilingual',
            locale: 'it-IT',
            language: 'Italian (Italy)',
            gender: 'male',
            description: 'A warm and pleasant voice, offering a soothing and comforting tone to all dialogues.',
            style: 'Cheerful'
        },
        {
            name: 'es-ES-Tristan:DragonHDLatestNeural',
            displayName: 'Tristan Dragon HD Latest',
            locale: 'es-ES',
            language: 'Spanish (Spain)',
            gender: 'male',
            description: 'HD version of Tristan, a trustworthy voice to deliver fact and information.',
            style: 'Formal'
        }
    ],
    female: [
        {
            name: 'es-ES-ArabellaMultilingualNeural',
            displayName: 'Arabella Multilingual',
            locale: 'es-ES',
            language: 'Spanish (Spain)',
            gender: 'female',
            description: 'A warm and pleasant voice, adding a touch of calm and comfort to any situation.',
            style: 'Cheerful',
            featured: true
        },
        {
            name: 'en-GB-SoniaNeural',
            displayName: 'Sonia',
            locale: 'en-GB',
            language: 'English (United Kingdom)',
            gender: 'female',
            description: 'A gentle and soft voice, providing a calm and soothing presence.',
            style: 'Gentle',
            popular: true
        },
        {
            name: 'it-IT-Isabella:DragonHDLatestNeural',
            displayName: 'Isabella Dragon HD',
            locale: 'it-IT',
            language: 'Italian (Italy)',
            gender: 'female',
            description: 'A calm and soothing adult voice with a dark, warm timbre that exudes tranquility and composure, perfect for creating a relaxed and engaging listening experience.',
            style: 'Calm'
        },
        {
            name: 'pl-PL-ZofiaNeural',
            displayName: 'Zofia',
            locale: 'pl-PL',
            language: 'Polish (Poland)',
            gender: 'female',
            description: 'A clear-sounding voice with great versatility that can adapt to any use case and speak in a way that\'s easy to understand.',
            style: 'Bright',
            popular: true
        },
        {
            name: 'pl-PL-AgnieszkaNeural',
            displayName: 'Agnieszka',
            locale: 'pl-PL',
            language: 'Polish (Poland)',
            gender: 'female',
            description: 'A Polish female with a professional, neutral tone.',
            style: 'Elegant'
        },
        {
            name: 'zh-HK-XiaoMinNeural',
            displayName: 'XiaoMin',
            locale: 'zh-HK',
            language: 'Chinese (Cantonese, Simplified)',
            gender: 'female',
            description: 'A bright, crisp, and confident voice, delivering clarity and assurance in every statement.',
            style: 'Bright'
        },
        {
            name: 'de-DE-SeraphinaMultilingualNeural',
            displayName: 'Seraphina',
            locale: 'de-DE',
            language: 'German (Germany)',
            gender: 'female',
            description: 'A casually charming voice, ideal for both casual chats and audiobooks, offering a relaxed yet engaging tone.',
            style: 'Bright'
        }
    ]
};
/**
 * Azure voice management utilities
 */
class AzureVoicesHelper {
    /**
     * Get all Azure voices
     */
    static getAzureVoices() {
        return [...exports.AVAILABLE_VOICES.male, ...exports.AVAILABLE_VOICES.female];
    }
    /**
     * Find Azure voice by name
     */
    static findAzureVoiceByName(name) {
        const allVoices = this.getAzureVoices();
        return allVoices.find(voice => voice.name === name) || null;
    }
    /**
     * Get Azure voices filtered by locale
     */
    static getAzureVoicesByLocale(locale) {
        const allVoices = this.getAzureVoices();
        return allVoices.filter(voice => voice.locale.startsWith(locale));
    }
    /**
     * Get Azure voices formatted for dropdown selection
     */
    static getVoiceOptionsForSelect() {
        const allVoices = this.getAzureVoices();
        return allVoices.map(voice => ({
            label: `🎤 ${voice.displayName} (${voice.language})`,
            value: voice.name
        }));
    }
    /**
     * Get default voice for a locale
     */
    static getDefaultVoiceForLocale(locale = 'en') {
        const localeVoices = this.getAzureVoicesByLocale(locale);
        if (localeVoices.length > 0) {
            return localeVoices[0];
        }
        // Fallback to first available voice
        const allVoices = this.getAzureVoices();
        return allVoices.length > 0 ? allVoices[0] : null;
    }
    /**
     * Get voices by gender
     */
    static getVoicesByGender(gender) {
        return exports.AVAILABLE_VOICES[gender];
    }
    /**
     * Get first male voice for a given language code
     */
    static getFirstMaleVoiceForLanguage(languageCode) {
        // Convert language code to locale prefix (e.g., 'en' -> 'en-', 'pl' -> 'pl-')
        const localePrefix = languageCode.toLowerCase() + '-';
        // Find first male voice that matches the language
        const maleVoice = exports.AVAILABLE_VOICES.male.find(voice => voice.locale.toLowerCase().startsWith(localePrefix));
        // If no exact match, try to find by common language mappings
        if (!maleVoice && languageCode) {
            const languageMap = {
                'en': 'en-gb', // English -> British English
                'de': 'de-de', // German -> German Germany
                'es': 'es-es', // Spanish -> Spanish Spain  
                'it': 'it-it', // Italian -> Italian Italy
                'pl': 'pl-pl', // Polish -> Polish Poland
                'zh': 'zh-hk' // Chinese -> Chinese Hong Kong
            };
            const mappedLocale = languageMap[languageCode.toLowerCase()];
            if (mappedLocale) {
                return exports.AVAILABLE_VOICES.male.find(voice => voice.locale.toLowerCase() === mappedLocale);
            }
        }
        return maleVoice || null;
    }
    /**
     * Convert translation language code to locale format
     */
    static languageCodeToLocale(languageCode) {
        const languageMap = {
            'en': 'en-GB',
            'de': 'de-DE',
            'es': 'es-ES',
            'it': 'it-IT',
            'pl': 'pl-PL',
            'zh': 'zh-HK'
        };
        return languageMap[languageCode.toLowerCase()] || 'en-GB';
    }
    /**
     * Load Azure voices (now returns hardcoded list)
     */
    static async loadAzureVoicesFromAPI(apiService) {
        const voices = this.getAzureVoices();
        console.log('🎤 [VoicesHelper] Loaded', voices.length, 'hardcoded Azure voices');
        return voices;
    }
}
exports.AzureVoicesHelper = AzureVoicesHelper;
// Legacy functions - now work with hardcoded Azure voices
function findVoiceById(voiceId) {
    const voice = AzureVoicesHelper.findAzureVoiceByName(voiceId);
    if (voice) {
        return {
            id: voice.name,
            nameKey: voice.displayName,
            descriptionKey: voice.description
        };
    }
    return null;
}
function getVoiceCategory(voiceId) {
    const voice = AzureVoicesHelper.findAzureVoiceByName(voiceId);
    return voice ? voice.gender : null;
}
function getVoicesByCategory(category) {
    const voices = AzureVoicesHelper.getVoicesByGender(category);
    return voices.map(voice => ({
        id: voice.name,
        nameKey: voice.displayName,
        descriptionKey: voice.description
    }));
}
function getAllVoices() {
    const voices = AzureVoicesHelper.getAzureVoices();
    return voices.map(voice => ({
        id: voice.name,
        nameKey: voice.displayName,
        descriptionKey: voice.description
    }));
}
function getVoiceById(id) {
    return findVoiceById(id) || undefined;
}
function getVoicesGroupedByGender() {
    return exports.AVAILABLE_VOICES;
}
function getVoicesForGender(gender) {
    if (!gender) {
        return exports.AVAILABLE_VOICES;
    }
    if (gender === 'male') {
        return {
            male: exports.AVAILABLE_VOICES.male,
            female: []
        };
    }
    if (gender === 'female') {
        return {
            male: [],
            female: exports.AVAILABLE_VOICES.female
        };
    }
    return exports.AVAILABLE_VOICES;
}
// Model-based voice chat detection (kept for avatar voice chat functionality)
const liveChats = [
    // Gemini models
    'gemini-2.5-flash',
    // OpenAI models
    'gpt-4-turbo',
    // Anthropic models
    'claude-sonnet-4'
];
exports.liveChats = liveChats;
const agentIds = {
    'gemini-2.5-flash': 'agent_6901k60jfygne29bsjwjen7dyswe',
    'gpt-4-turbo': 'agent_9301k60jne1xfe7rwpss2xvkabxz',
    'claude-sonnet-4': 'agent_7601k60jqt1behqrtezh6jbyr64k'
};
exports.agentIds = agentIds;
function isModelInElevenLabs(modelValue) {
    return liveChats.some(elevenLabsModel => {
        var _a;
        const modelKey = (_a = modelValue.split('/').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        return modelKey === elevenLabsModel.toLowerCase();
    });
}
function isAvatarTalking(avatar) {
    var _a, _b;
    return avatar.isVoiceAgent && ((_b = (_a = avatar.model) === null || _a === void 0 ? void 0 : _a.model) === null || _b === void 0 ? void 0 : _b.value) ?
        isModelInElevenLabs(avatar.model.model.value) : false;
}
